import React from 'react';
import { Card, Text, mergeClassNames } from '@clockout-dev/design-elements';
import styles from './styles/enrollment-funnel.module.scss';

const FIGMA_STEP_HEIGHTS = [104, 106, 94, 82, 70, 70, 70];

/** Placeholder rows shown while the (separately-loaded) funnel is fetching. */
const SKELETON_STEPS = FIGMA_STEP_HEIGHTS.slice(0, 6).map((_, i) => ({ key: `sk-${i}` }));

function EnrollmentFunnel({ title, subtitle, steps = [], groups = [], cohort = {}, loading = false }) {
    const showSkeleton = loading && steps.length === 0;

    return (
        <Card className={styles.card}>
            <div className={styles.header}>
                <Text as="span" className={styles.title}>{title}</Text>
                {subtitle && <Text as="span" className={styles.subtitle}>{subtitle}</Text>}
            </div>

            <div className={styles.scroller}>
                <div className={styles.funnelInner}>
                    <div className={styles.funnel}>
                        {showSkeleton && SKELETON_STEPS.map((step, index) => {
                            const height = FIGMA_STEP_HEIGHTS[index] || FIGMA_STEP_HEIGHTS[FIGMA_STEP_HEIGHTS.length - 1];
                            return (
                                <div
                                    key={step.key}
                                    className={mergeClassNames([
                                        styles.step,
                                        styles.stepSkeleton,
                                        index === 0 && styles.firstStep,
                                    ])}
                                    style={{ height }}
                                    aria-hidden="true"
                                />
                            );
                        })}
                        {!showSkeleton && steps.map((step, index) => {
                            const height = FIGMA_STEP_HEIGHTS[index] || FIGMA_STEP_HEIGHTS[FIGMA_STEP_HEIGHTS.length - 1];
                            const value = step.value == null ? 0 : step.value;
                            const clickable = step.drilldown && cohort.onOpen;

                            return (
                                <div
                                    key={step.key}
                                    className={mergeClassNames([
                                        styles.step,
                                        index === 0 && styles.firstStep,
                                        clickable && styles.clickable,
                                    ])}
                                    style={{ height, background: step.fill }}
                                    role={clickable ? 'button' : undefined}
                                    tabIndex={clickable ? 0 : undefined}
                                    aria-label={clickable ? `View ${step.label} users` : undefined}
                                    onClick={clickable ? () => cohort.onOpen(step.drilldown) : undefined}
                                    onKeyDown={
                                        clickable
                                            ? (e) => {
                                                  if (e.key === 'Enter' || e.key === ' ') {
                                                      e.preventDefault();
                                                      cohort.onOpen(step.drilldown);
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    <Text as="span" className={styles.value}>{value}</Text>
                                    <Text as="span" className={styles.label}>{step.label}</Text>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.groups}>
                        {groups.map((group) => (
                            <span key={group.label} className={styles.group} style={{ flexGrow: group.span }}>
                                {group.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </Card>
    );
}

export default EnrollmentFunnel;
